/**
 * Crew ▸ Crew Lists (IMO FAL Form 5; ortak 2026-10-26 madde 4; ADR 0130 §B): liste geçmişi, yeni
 * liste (gemideki personelden seçim, üst alanlar gemi ve seferden), düz biçim PDF ve yazdırma.
 * PDF'in alan adları FAL Form 5'in numaralarıyla; Türkçede iki dilli (form limanda İngilizce okunur).
 */

export const crewListEn = {
  description:
    'Crew lists in IMO FAL Form 5 layout: choose the crew on board, check the details and print. Saved lists stay in the history.',
  error: 'The crew lists could not be loaded.',
  empty: 'No crew list has been saved yet.',
  newList: 'New Crew List',
  allVessels: 'All vessels',
  vessel: 'Vessel',
  chooseVessel: 'Choose a vessel to prepare a crew list.',
  columns: {
    created: 'Created',
    by: 'Created by',
    vessel: 'Vessel',
    direction: 'Arrival / Departure',
    port: 'Port',
    date: 'Date',
    voyage: 'Voyage',
    crew: 'Crew',
    source: 'Source',
    actions: 'Actions',
  },
  origin: { ship: 'Vessel', office: 'Office' },
  direction: { arrival: 'Arrival', departure: 'Departure' },
  docType: { passport: 'Passport', seaman_book: "Seaman's Book" },
  actions: {
    pdf: 'Download PDF',
    print: 'Print',
    save: 'Save and Download PDF',
    savePrint: 'Save and Print',
    cancel: 'Cancel',
    back: 'Back to lists',
    moveUp: 'Move {{name}} up',
    moveDown: 'Move {{name}} down',
    sortByRank: 'Sort by rank',
    selectAll: 'Select all crew',
    select: 'Include {{name}}',
  },
  editor: {
    title: 'New Crew List',
    // Personel kartından gelen değer bu listede değiştirildi (ADR 0131 §2)
    edited: 'Edited',
    cardValue: 'Crew card: {{value}}',
    header: 'List details',
    headerHint:
      'Filled from the vessel record and the selected voyage; change any field if needed. Empty fields stay empty on the list.',
    crew: 'Crew on board',
    crewHint:
      'Everyone on board today is selected. Untick a person to leave them out; the order is the rank order unless you move rows.',
    selected: '{{count}} of {{total}} selected',
    voyage: 'Voyage',
    noVoyage: 'No voyage',
    direction: 'Arrival / Departure',
    docType: 'Identity document',
    docTypeAll: 'Use for all',
    noCrew: 'Nobody is assigned to this vessel today.',
    savedNote:
      'A saved list cannot be changed; prepare a new list if something is wrong. Nationality and birth details are suggested from each person’s last list.',
    noPassengers: 'Passengers and guests are not entered here (IMO FAL Form 6); see ADR 0130.',
    errors: {
      REQUIRED: 'Required',
      INVALID_DATE: 'Invalid date',
      TOO_LONG: 'Too long',
      noneSelected: 'Select at least one person.',
    },
  },
  fields: {
    vesselName: '1.1 Name of ship',
    imo: '1.2 IMO number',
    callSign: '1.3 Call sign',
    voyageNo: '1.4 Voyage number',
    port: '2. Port of arrival/departure',
    date: '3. Date of arrival/departure',
    flag: '4. Flag State of ship',
    lastPort: '5. Last port of call',
    no: '6. No.',
    name: '7. Family name, given names',
    familyName: 'Family name',
    givenNames: 'Given names',
    rank: '8. Rank or rating',
    nationality: '9. Nationality',
    birth: '10. Date and place of birth',
    birthDate: 'Date of birth',
    birthPlace: 'Place of birth',
    document: '11. Nature and number of identity document',
    docNumber: 'Document number',
  },
  pdf: {
    title: 'CREW LIST',
    subtitle: 'IMO FAL Form 5',
    arrival: 'Arrival',
    departure: 'Departure',
    page: 'Page {{page}} of {{pages}}',
    signature: '12. Date and signature by master, authorized agent or officer',
    errors: {
      failed: 'The PDF could not be created.',
    },
  },
  saved: 'Crew list saved.',
  errors: {
    CREW_NOT_ON_BOARD: 'A selected person is no longer on board. Reload the list and try again.',
    INVALID_CREW_LIST: 'Check the highlighted fields.',
    FORBIDDEN: 'You do not have permission to prepare crew lists.',
    failed: 'The crew list could not be saved or printed. Try again.',
  },
};

export const crewListTr: typeof crewListEn = {
  description:
    'IMO FAL Form 5 düzeninde mürettebat listeleri: gemideki personeli seçin, bilgileri kontrol edin ve yazdırın. Kaydedilen listeler geçmişte kalır.',
  error: 'Mürettebat listeleri yüklenemedi.',
  empty: 'Henüz kaydedilmiş mürettebat listesi yok.',
  newList: 'Yeni Mürettebat Listesi',
  allVessels: 'Bütün gemiler',
  vessel: 'Gemi',
  chooseVessel: 'Liste hazırlamak için bir gemi seçin.',
  columns: {
    created: 'Oluşturulma',
    by: 'Oluşturan',
    vessel: 'Gemi',
    direction: 'Varış / Kalkış',
    port: 'Liman',
    date: 'Tarih',
    voyage: 'Sefer',
    crew: 'Kişi',
    source: 'Kaynak',
    actions: 'İşlemler',
  },
  origin: { ship: 'Gemi', office: 'Ofis' },
  direction: { arrival: 'Varış', departure: 'Kalkış' },
  docType: { passport: 'Pasaport', seaman_book: 'Gemiadamı cüzdanı' },
  actions: {
    pdf: 'PDF indir',
    print: 'Yazdır',
    save: 'Kaydet ve PDF indir',
    savePrint: 'Kaydet ve yazdır',
    cancel: 'Vazgeç',
    back: 'Listelere dön',
    moveUp: '{{name}} yukarı',
    moveDown: '{{name}} aşağı',
    sortByRank: 'Rütbeye göre sırala',
    selectAll: 'Bütün personeli seç',
    select: '{{name}} listeye girsin',
  },
  editor: {
    title: 'Yeni Mürettebat Listesi',
    // Personel kartından gelen değer bu listede değiştirildi (ADR 0131 §2)
    edited: 'Değiştirildi',
    cardValue: 'Personel kartı: {{value}}',
    header: 'Liste bilgileri',
    headerHint:
      'Gemi kaydından ve seçilen seferden doldu; gerekirse değiştirin. Boş alanlar listede boş kalır.',
    crew: 'Gemideki personel',
    crewHint:
      'Bugün gemide olan herkes seçili. Listeye girmeyecek kişinin işaretini kaldırın; satırları taşımadıkça sıra rütbe sırasıdır.',
    selected: '{{total}} kişiden {{count}} seçili',
    voyage: 'Sefer',
    noVoyage: 'Sefer yok',
    direction: 'Varış / Kalkış',
    docType: 'Kimlik belgesi',
    docTypeAll: 'Hepsine uygula',
    noCrew: 'Bugün bu gemiye atanmış kimse yok.',
    savedNote:
      'Kaydedilen liste değiştirilemez; yanlışlık varsa yeni liste hazırlayın. Uyruk ve doğum bilgisi kişinin son listesinden önerilir.',
    noPassengers: 'Yolcu ve misafirler buraya girilmez (IMO FAL Form 6); bkz. ADR 0130.',
    errors: {
      REQUIRED: 'Zorunlu',
      INVALID_DATE: 'Geçersiz tarih',
      TOO_LONG: 'Çok uzun',
      noneSelected: 'En az bir kişi seçin.',
    },
  },
  fields: {
    vesselName: '1.1 Name of ship / Gemi adı',
    imo: '1.2 IMO number / IMO numarası',
    callSign: '1.3 Call sign / Çağrı işareti',
    voyageNo: '1.4 Voyage number / Sefer numarası',
    port: '2. Port of arrival/departure / Varış/kalkış limanı',
    date: '3. Date of arrival/departure / Varış/kalkış tarihi',
    flag: '4. Flag State of ship / Bayrak devleti',
    lastPort: '5. Last port of call / Son uğranan liman',
    no: '6. No.',
    name: '7. Family name, given names / Soyadı, adı',
    familyName: 'Soyadı',
    givenNames: 'Adı',
    rank: '8. Rank or rating / Rütbe',
    nationality: '9. Nationality / Uyruk',
    birth: '10. Date and place of birth / Doğum tarihi ve yeri',
    birthDate: 'Doğum tarihi',
    birthPlace: 'Doğum yeri',
    document: '11. Nature and number of identity document / Kimlik belgesi türü ve numarası',
    docNumber: 'Belge numarası',
  },
  pdf: {
    title: 'CREW LIST / MÜRETTEBAT LİSTESİ',
    subtitle: 'IMO FAL Form 5',
    arrival: 'Arrival / Varış',
    departure: 'Departure / Kalkış',
    page: 'Page / Sayfa {{page}} / {{pages}}',
    signature:
      '12. Date and signature by master, authorized agent or officer / Kaptanın, yetkili acentenin ya da zabitin tarih ve imzası',
    errors: {
      failed: 'PDF oluşturulamadı.',
    },
  },
  saved: 'Mürettebat listesi kaydedildi.',
  errors: {
    CREW_NOT_ON_BOARD: 'Seçilen bir kişi artık gemide değil. Listeyi yenileyip yeniden deneyin.',
    INVALID_CREW_LIST: 'İşaretli alanları kontrol edin.',
    FORBIDDEN: 'Mürettebat listesi hazırlama yetkiniz yok.',
    failed: 'Mürettebat listesi kaydedilemedi ya da yazdırılamadı. Yeniden deneyin.',
  },
};
